import { observer } from "mobx-react-lite";
import { Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import ServiceLegalRoute from "../components/ServiceLegalRoute";
// NotFound is small, keep it eager loaded
import NotFound from "../modules/NotFound";
import { Flex, Spin } from "antd";
import {
  CookieSettings,
  CurrentLegalNoticeDocument,
  DataServices,
  Docs,
  ForgotPassword,
  HpcFlows,
  Login,
  MyProjects,
  MyTicketsDashboard,
  ProjectDetail,
  Projects,
  Profile,
  ResetPassword,
  Signup,
  TicketsDashboard,
  Welcome,
} from "./lazyPages";
import { useRouteAccess } from "./useRouteAccess";
import { renderAdminRoutes } from "./adminRoutes";

const AppRoutes = observer(function AppRoutes() {
  const {
    isLoading,
    user,
    canAccessAdminArea,
    canManageTickets,
    canManageLegalDocuments,
    canMoveMembers,
    canAccessMyProjectsRoute,
    canAccessMyTicketsRoute,
    projectServicesRoutes,
    canUseHpcFlows,
    customServiceRoutes,
  } = useRouteAccess();

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ width: "100%" }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route
        path="/data-services"
        element={
          <ProtectedRoute>
            <DataServices />
          </ProtectedRoute>
        }
      />
      <Route path="/legal-notice" element={<CurrentLegalNoticeDocument />} />
      <Route
        path="/terms-and-conditions"
        element={<Navigate to="/legal-notice" replace />}
      />
      <Route
        path="/privacy-policy"
        element={<Navigate to="/legal-notice" replace />}
      />
      <Route path="/cookie-settings" element={<CookieSettings />} />

      {projectServicesRoutes.map(({ path, component }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <ServiceLegalRoute>{component}</ServiceLegalRoute>
            </ProtectedRoute>
          }
        />
      ))}

      {canUseHpcFlows && (
        <Route
          path="/hpc-flows/*"
          element={
            <ProtectedRoute>
              <ServiceLegalRoute servicePath="/hpc-flows">
                <HpcFlows />
              </ServiceLegalRoute>
            </ProtectedRoute>
          }
        />
      )}

      {customServiceRoutes.map(({ path, component }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <ServiceLegalRoute>{component}</ServiceLegalRoute>
            </ProtectedRoute>
          }
        />
      ))}

      <Route
        path="/docs"
        element={
          <ProtectedRoute>
            <Docs />
          </ProtectedRoute>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* OpenID disabled — no longer supported
      <Route path="/auth/callback" element={<AuthCallback />} /> */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      {renderAdminRoutes({
        user,
        canManageLegalDocuments,
        canAccessAdminArea,
        canMoveMembers,
      })}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      {canAccessMyProjectsRoute && (
        <Route
          path="/my-projects"
          element={
            <ProtectedRoute>
              <MyProjects />
            </ProtectedRoute>
          }
        />
      )}
      {canManageTickets && (
        <Route
          path="/admin/tickets"
          element={
            <ProtectedRoute>
              <TicketsDashboard />
            </ProtectedRoute>
          }
        />
      )}
      {canAccessMyTicketsRoute && (
        <Route
          path="/my-tickets"
          element={
            <ProtectedRoute>
              <MyTicketsDashboard />
            </ProtectedRoute>
          }
        />
      )}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
});

export default AppRoutes;
